// Cookie Consent Banner Component
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a cookie choice
    const cookieConsent = localStorage.getItem("cookieConsent");
    if (!cookieConsent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem("cookieConsent", "all");
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setShowBanner(false);
    // Enable analytics if needed
    if (window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
      });
    }
  };

  const handleAcceptEssential = () => {
    localStorage.setItem("cookieConsent", "essential");
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setShowBanner(false);
    // Disable analytics
    if (window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "denied",
      });
    }
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
    >
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gold/20 overflow-hidden">
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-4">
            <h2 id="cookie-consent-title" className="font-heading text-xl text-neutral-800">
              Cookie Preferences
            </h2>
            <button
              onClick={handleAcceptEssential}
              className="text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Close cookie banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <p id="cookie-consent-description" className="text-neutral-600 text-sm md:text-base mb-6">
            We use cookies to enhance your browsing experience, analyse site traffic, and understand where our visitors come from. 
            By clicking "Accept All", you consent to our use of cookies. You can also choose to accept only essential cookies 
            which are necessary for the website to function properly.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <button
              onClick={handleAcceptAll}
              className="flex-1 bg-gold hover:bg-gold-dark text-white px-6 py-3 rounded-full font-medium transition-colors"
              data-testid="cookie-accept-all"
            >
              Accept All Cookies
            </button>
            <button
              onClick={handleAcceptEssential}
              className="flex-1 border border-neutral-300 hover:border-gold text-neutral-700 hover:text-gold px-6 py-3 rounded-full font-medium transition-colors"
              data-testid="cookie-accept-essential"
            >
              Essential Only
            </button>
          </div>
          
          <p className="text-xs text-neutral-500 text-center">
            Learn more about how we use cookies in our{" "}
            <Link to="/cookie-policy" className="text-gold hover:underline">
              Cookie Policy
            </Link>{" "}
            and{" "}
            <Link to="/privacy-policy" className="text-gold hover:underline">
              Privacy Policy
            </Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
